import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { Calendar, ChevronDown, Clock } from 'lucide-react';
import PageLayout from '../../components/layouts/PageLayout';
import AnalyticsExportMenu, {
  AnalyticsExportFormat,
} from '../../components/analytics/AnalyticsExportMenu';
import { authService } from '../../auth/authService';
import { canAccessPage } from '../../utils/permissions';
import { getDefaultRoute } from '../../utils/getDefaultRoute';
import { ICON_STROKE_WIDTH } from '../../constants';
import {
  getReportDeliveryAnalytics,
  ReportDeliveryChartPoint,
  ReportDeliveryDetailRow,
  ReportDeliveryModalityRow,
  ReportDeliverySummary,
} from '../../services/reportDeliveryAnalyticsService';
import userService from '../../services/userService';
import facilityService from '../../services/facilityService';
import { STORAGE_KEYS } from '../../config/api.config';
import { exportReportDeliveryAnalytics } from '../../utils/reportDeliveryAnalyticsExport';
import '../../components/analytics/analytics-theme.css';
import './ReportDeliveryAnalytics.css';

function todayIsoDate(): string {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

const EMPTY_SUMMARY: ReportDeliverySummary = {
  totalStudies: 0,
  totalPending: 0,
  totalReportsDelivered: 0,
  averageDeliveryTimeMinutes: 0,
  averageDeliveryTimeFormatted: '0 min',
  delayedReports: 0,
  pendingOverdue: 0,
  lateDelivered: 0,
};

const ALL_OPTION = { value: '', label: 'All' };

const STATIC_FILTER_OPTIONS = {
  modality: [
    { ...ALL_OPTION, label: 'All modalities' },
    { value: 'CT', label: 'CT' },
    { value: 'MR', label: 'MRI' },
    { value: 'US', label: 'Ultrasound' },
    { value: 'XR', label: 'X-ray' },
  ],
  priority: [
    { ...ALL_OPTION, label: 'All priorities' },
    { value: 'routine', label: 'Routine' },
    { value: 'vip', label: 'VIP' },
    { value: 'urgent', label: 'Urgent' },
    { value: 'mlc', label: 'MLC' },
  ],
  channel: [
    { ...ALL_OPTION, label: 'All channels' },
    { value: 'SMS', label: 'SMS' },
    { value: 'WHATSAPP', label: 'WhatsApp' },
  ],
};

const CHART_HEIGHT = 340;

function formatChartDuration(minutes: number): string {
  if (!Number.isFinite(minutes) || minutes <= 0) {
    return '0 min';
  }
  const rounded = Math.round(minutes);
  if (rounded < 60) {
    return `${rounded} min`;
  }
  const hours = Math.floor(rounded / 60);
  const rem = rounded % 60;
  if (rem === 0) {
    return `${hours}h`;
  }
  return `${hours}h ${rem}m`;
}

function computeChartYAxis(
  points: ReportDeliveryChartPoint[],
  averageMinutes = 0
): { yMax: number; yTicks: number[] } {
  const dataMax = points.reduce((max, p) => Math.max(max, p.minutes), 0);
  const maxMinutes = Math.max(dataMax, averageMinutes, 30);
  let step = 50;
  if (maxMinutes > 200) {
    step = 100;
  }
  if (maxMinutes > 500) {
    step = 250;
  }
  if (maxMinutes > 1000) {
    step = 500;
  }
  const yMax = Math.max(step, Math.ceil(maxMinutes / step) * step);
  const yTicks: number[] = [];
  for (let tick = 0; tick <= yMax; tick += step) {
    yTicks.push(tick);
  }
  return { yMax, yTicks };
}

function hasSharpZeroJump(points: ReportDeliveryChartPoint[]): boolean {
  for (let index = 1; index < points.length; index += 1) {
    const previous = points[index - 1]?.minutes ?? 0;
    const current = points[index]?.minutes ?? 0;
    if ((previous === 0 && current > 0) || (current === 0 && previous > 0)) {
      return true;
    }
  }
  return false;
}

type ChartCoord = { x: number; y: number };

function clampChartY(y: number, top: number, bottom: number): number {
  return Math.max(top, Math.min(bottom, y));
}

function buildLinearLinePath(coords: ChartCoord[]): string {
  if (coords.length === 0) {
    return '';
  }
  return coords.map((coord, index) => `${index === 0 ? 'M' : 'L'} ${coord.x} ${coord.y}`).join(' ');
}

function buildSmoothLinePath(coords: ChartCoord[], innerWidth?: number, paddingLeft = 0): string {
  if (coords.length === 0) {
    return '';
  }
  if (coords.length === 1) {
    const { x, y } = coords[0];
    const span = Math.max(80, (innerWidth ?? 0) * 0.35);
    const x1 = Math.max(paddingLeft, x - span / 2);
    const x2 = x + span / 2;
    return `M ${x1} ${y} L ${x2} ${y}`;
  }

  if (coords.length === 2) {
    return `M ${coords[0].x} ${coords[0].y} L ${coords[1].x} ${coords[1].y}`;
  }

  let path = `M ${coords[0].x} ${coords[0].y}`;
  for (let i = 0; i < coords.length - 1; i += 1) {
    const p0 = coords[Math.max(0, i - 1)];
    const p1 = coords[i];
    const p2 = coords[i + 1];
    const p3 = coords[Math.min(coords.length - 1, i + 2)];

    const cp1x = p1.x + (p2.x - p0.x) / 6;
    const cp1y = p1.y + (p2.y - p0.y) / 6;
    const cp2x = p2.x - (p3.x - p1.x) / 6;
    const cp2y = p2.y - (p3.y - p1.y) / 6;

    path += ` C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${p2.x} ${p2.y}`;
  }
  return path;
}

function buildChartLinePath(
  coords: ChartCoord[],
  points: ReportDeliveryChartPoint[],
  innerWidth?: number,
  paddingLeft = 0
): string {
  if (hasSharpZeroJump(points)) {
    return buildLinearLinePath(coords);
  }
  return buildSmoothLinePath(coords, innerWidth, paddingLeft);
}

function getXAxisLabelIndices(pointCount: number, innerWidth: number): number[] {
  const maxLabels = Math.max(6, Math.floor(innerWidth / 76));
  const step = Math.max(1, Math.ceil(pointCount / maxLabels));
  const indices: number[] = [];

  for (let index = 0; index < pointCount; index += step) {
    indices.push(index);
  }

  if (indices[indices.length - 1] !== pointCount - 1) {
    indices.push(pointCount - 1);
  }

  return indices;
}

function buildSmoothAreaPath(
  coords: ChartCoord[],
  bottomY: number,
  points: ReportDeliveryChartPoint[],
  innerWidth?: number,
  paddingLeft = 0
): string {
  if (coords.length === 0) {
    return '';
  }
  const line = buildChartLinePath(coords, points, innerWidth, paddingLeft);
  if (coords.length === 1) {
    const { x, y } = coords[0];
    const span = Math.max(80, (innerWidth ?? 0) * 0.35);
    const x1 = Math.max(paddingLeft, x - span / 2);
    const x2 = x + span / 2;
    return `M ${x1} ${y} L ${x2} ${y} L ${x2} ${bottomY} L ${x1} ${bottomY} Z`;
  }
  const last = coords[coords.length - 1];
  const first = coords[0];
  return `${line} L ${last.x} ${bottomY} L ${first.x} ${bottomY} Z`;
}

function computeTooltipStyle(
  coord: ChartCoord,
  chart: {
    width: number;
    height: number;
    padding: { top: number };
    innerH: number;
  },
  containerWidth: number
): { left: string; top: string; transform: string } {
  const leftPct = (coord.x / chart.width) * 100;
  const topPct = (coord.y / chart.height) * 100;
  const nearTop = coord.y < chart.padding.top + chart.innerH * 0.22;
  const tooltipWidthPct = containerWidth > 0 ? Math.min(28, (168 / containerWidth) * 100) : 20;
  const clampedLeft = Math.max(tooltipWidthPct / 2, Math.min(100 - tooltipWidthPct / 2, leftPct));

  return {
    left: `${clampedLeft}%`,
    top: `${topPct}%`,
    transform: nearTop ? 'translate(-50%, 16px)' : 'translate(-50%, calc(-100% - 16px))',
  };
}

function formatFilterDate(isoDate: string): string {
  if (!isoDate) {
    return '';
  }
  const [year, month, day] = isoDate.split('-');
  return `${day}/${month}/${year}`;
}

function clampIsoDate(value: string, min?: string, max?: string): string {
  let result = value;
  if (max && result > max) {
    result = max;
  }
  if (min && result < min) {
    result = min;
  }
  return result;
}

function RdaFilterDate({
  label,
  value,
  onChange,
  min,
  max,
}: Readonly<{
  label: string;
  value: string;
  onChange: (value: string) => void;
  min?: string;
  max?: string;
}>) {
  const inputRef = useRef<HTMLInputElement>(null);

  return (
    <div className="rda-filter-field">
      <label
        className="rda-filter-label"
        htmlFor={`rda-date-${label}`}
      >
        {label}
      </label>
      <div
        className="rda-filter-control rda-filter-control--date"
        onClick={() => inputRef.current?.showPicker?.()}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            inputRef.current?.showPicker?.();
          }
        }}
        role="button"
        tabIndex={0}
      >
        <span className="rda-filter-value">{formatFilterDate(value)}</span>
        <Calendar
          className="rda-filter-icon"
          size={16}
          strokeWidth={ICON_STROKE_WIDTH}
          aria-hidden
        />
        <input
          ref={inputRef}
          id={`rda-date-${label}`}
          type="date"
          className="rda-filter-date-input"
          value={value}
          min={min}
          max={max}
          onChange={e => onChange(e.target.value)}
        />
      </div>
    </div>
  );
}

function RdaFilterSelect({
  label,
  value,
  onChange,
  options,
}: Readonly<{
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: { value: string; label: string }[];
}>) {
  const selectId = `rda-select-${label.replace(/\s+/g, '-').toLowerCase()}`;

  return (
    <div className="rda-filter-field">
      <label
        className="rda-filter-label"
        htmlFor={selectId}
      >
        {label}
      </label>
      <div className="rda-filter-control rda-filter-control--select">
        <select
          id={selectId}
          className="rda-filter-select"
          value={value}
          onChange={e => onChange(e.target.value)}
        >
          {options.map(option => (
            <option
              key={option.value || '__all__'}
              value={option.value}
            >
              {option.label}
            </option>
          ))}
        </select>
        <ChevronDown
          className="rda-filter-icon rda-filter-icon--select"
          size={16}
          strokeWidth={ICON_STROKE_WIDTH}
          aria-hidden
        />
      </div>
    </div>
  );
}

function DeliveryTimeLineChart({
  points,
  yMax,
  yTicks,
  averageMinutes = 0,
}: Readonly<{
  points: ReportDeliveryChartPoint[];
  yMax: number;
  yTicks: number[];
  averageMinutes?: number;
}>) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  useEffect(() => {
    const element = wrapRef.current;
    if (!element) {
      return undefined;
    }

    const updateWidth = () => {
      setContainerWidth(Math.max(element.clientWidth, 320));
    };

    updateWidth();
    const observer = new ResizeObserver(updateWidth);
    observer.observe(element);
    window.addEventListener('resize', updateWidth);

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', updateWidth);
    };
  }, []);

  const chart = useMemo(() => {
    const width = containerWidth || 320;
    const height = CHART_HEIGHT;
    const padding = { top: 16, right: 20, bottom: 56, left: 56 };
    const innerW = width - padding.left - padding.right;
    const innerH = height - padding.top - padding.bottom;
    const bottomY = padding.top + innerH;
    const yAxisLabelX = 12;
    const yTickLabelX = padding.left - 10;

    const xStep = points.length > 1 ? innerW / (points.length - 1) : 0;
    const yScale = (value: number) => padding.top + innerH - (value / yMax) * innerH;

    const coords: ChartCoord[] = points.map((point, index) => ({
      x: points.length === 1 ? padding.left + innerW / 2 : padding.left + index * xStep,
      y: clampChartY(yScale(point.minutes), padding.top, bottomY),
    }));

    const xLabelIndices = getXAxisLabelIndices(points.length, innerW);
    const averageY =
      averageMinutes > 0 ? clampChartY(yScale(averageMinutes), padding.top, bottomY) : null;

    return {
      width,
      height,
      padding,
      innerH,
      innerW,
      bottomY,
      yAxisLabelX,
      yTickLabelX,
      coords,
      xLabelIndices,
      averageY,
      linePath: buildChartLinePath(coords, points, innerW, padding.left),
      areaPath: buildSmoothAreaPath(coords, bottomY, points, innerW, padding.left),
    };
  }, [points, containerWidth, yMax, averageMinutes]);

  const handlePointerMove = (clientX: number, target: SVGRectElement) => {
    const svg = target.ownerSVGElement;
    if (!svg || chart.width <= 0) {
      return;
    }

    const rect = svg.getBoundingClientRect();
    const svgX = ((clientX - rect.left) / rect.width) * chart.width;

    let nearestIndex = 0;
    let nearestDistance = Number.POSITIVE_INFINITY;
    chart.coords.forEach((coord, index) => {
      const distance = Math.abs(coord.x - svgX);
      if (distance < nearestDistance) {
        nearestDistance = distance;
        nearestIndex = index;
      }
    });

    setHoveredIndex(nearestIndex);
  };

  const activePoint = hoveredIndex !== null ? points[hoveredIndex] : null;
  const activeCoord = hoveredIndex !== null ? chart.coords[hoveredIndex] : null;

  return (
    <div
      className="rda-chart-wrap"
      ref={wrapRef}
    >
      {containerWidth > 0 && (
        <svg
          className="rda-chart-svg"
          viewBox={`0 0 ${chart.width} ${chart.height}`}
          preserveAspectRatio="none"
          role="img"
          aria-label="Overall delivery time trend chart"
        >
          <defs>
            <linearGradient
              id="rda-area-gradient"
              x1="0"
              y1="0"
              x2="0"
              y2="1"
            >
              <stop
                offset="0%"
                stopColor="#38bdf8"
                stopOpacity="0.14"
              />
              <stop
                offset="100%"
                stopColor="#38bdf8"
                stopOpacity="0"
              />
            </linearGradient>
          </defs>

          {yTicks.map(tick => {
            const y = chart.padding.top + chart.innerH - (tick / yMax) * chart.innerH;
            return (
              <g key={tick}>
                <line
                  className="rda-chart-grid-line"
                  x1={chart.padding.left}
                  y1={y}
                  x2={chart.width - chart.padding.right}
                  y2={y}
                />
                <text
                  className="rda-chart-axis-label"
                  x={chart.yTickLabelX}
                  y={y + 4}
                  textAnchor="end"
                >
                  {tick}
                </text>
              </g>
            );
          })}

          {chart.averageY != null && averageMinutes > 0 && (
            <line
              className="rda-chart-threshold"
              x1={chart.padding.left}
              y1={chart.averageY}
              x2={chart.width - chart.padding.right}
              y2={chart.averageY}
            />
          )}

          <text
            className="rda-chart-axis-label rda-chart-axis-label--y"
            x={chart.yAxisLabelX}
            y={chart.padding.top + chart.innerH / 2}
            textAnchor="middle"
            transform={`rotate(-90, ${chart.yAxisLabelX}, ${chart.padding.top + chart.innerH / 2})`}
          >
            min
          </text>

          {chart.xLabelIndices.map(index => {
            const point = points[index];
            const isFirst = index === 0;
            const isLast = index === points.length - 1;
            const labelX = isFirst
              ? chart.padding.left
              : isLast
                ? chart.width - chart.padding.right
                : chart.coords[index].x;

            return (
              <text
                key={point.date}
                className="rda-chart-axis-label rda-chart-axis-label--x"
                x={labelX}
                y={chart.height - 18}
                textAnchor={isFirst ? 'start' : isLast ? 'end' : 'middle'}
              >
                {point.date}
              </text>
            );
          })}

          <path
            className="rda-chart-area"
            d={chart.areaPath}
          />
          <path
            className="rda-chart-line"
            d={chart.linePath}
          />

          {activeCoord && (
            <>
              <line
                className="rda-chart-hover-line"
                x1={activeCoord.x}
                y1={chart.padding.top}
                x2={activeCoord.x}
                y2={chart.bottomY}
              />
              <circle
                className="rda-chart-hover-dot"
                cx={activeCoord.x}
                cy={activeCoord.y}
                r={5}
              />
            </>
          )}

          <rect
            className="rda-chart-overlay"
            x={chart.padding.left}
            y={chart.padding.top}
            width={chart.width - chart.padding.left - chart.padding.right}
            height={chart.innerH}
            onMouseMove={e => handlePointerMove(e.clientX, e.currentTarget)}
            onMouseLeave={() => setHoveredIndex(null)}
          />
        </svg>
      )}

      {activePoint && activeCoord && (
        <div
          className="rda-chart-tooltip"
          style={computeTooltipStyle(activeCoord, chart, containerWidth)}
        >
          <div className="rda-chart-tooltip-date">{activePoint.date}</div>
          <div className="rda-chart-tooltip-value">
            Median {formatChartDuration(activePoint.minutes)}
            {activePoint.reportCount != null && activePoint.reportCount > 0
              ? ` (${activePoint.reportCount} report${activePoint.reportCount === 1 ? '' : 's'})`
              : ''}
          </div>
        </div>
      )}
    </div>
  );
}

function labelForOption(
  options: { value: string; label: string }[],
  value: string,
  fallback: string
): string {
  if (!value) {
    return fallback;
  }
  return options.find(option => option.value === value)?.label || value;
}

export function ReportDeliveryAnalytics() {
  const [fromDate, setFromDate] = useState(todayIsoDate);
  const [toDate, setToDate] = useState(todayIsoDate);
  const [facility, setFacility] = useState('');
  const [modality, setModality] = useState('');
  const [priority, setPriority] = useState('');
  const [radiologist, setRadiologist] = useState('');
  const [channel, setChannel] = useState('');
  const [facilityOptions, setFacilityOptions] = useState<{ value: string; label: string }[]>([
    { value: '', label: 'All facilities' },
  ]);
  const [radiologistOptions, setRadiologistOptions] = useState<{ value: string; label: string }[]>([
    { value: '', label: 'All radiologists' },
  ]);
  const [summary, setSummary] = useState<ReportDeliverySummary>(EMPTY_SUMMARY);
  const [chartPoints, setChartPoints] = useState<ReportDeliveryChartPoint[]>([]);
  const [modalityRows, setModalityRows] = useState<ReportDeliveryModalityRow[]>([]);
  const [detailRows, setDetailRows] = useState<ReportDeliveryDetailRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [warnings, setWarnings] = useState<string[]>([]);

  const chartAxis = useMemo(
    () => computeChartYAxis(chartPoints, summary.averageDeliveryTimeMinutes),
    [chartPoints, summary.averageDeliveryTimeMinutes]
  );
  const maxSelectableDate = todayIsoDate();

  const handleFromDateChange = (nextFrom: string) => {
    const clamped = clampIsoDate(nextFrom, undefined, maxSelectableDate);
    const finalFrom =
      toDate && clamped > toDate ? clampIsoDate(toDate, undefined, maxSelectableDate) : clamped;
    setFromDate(finalFrom);
    if (toDate && finalFrom > toDate) {
      setToDate(finalFrom);
    }
  };

  const handleToDateChange = (nextTo: string) => {
    const clamped = clampIsoDate(nextTo, fromDate || undefined, maxSelectableDate);
    setToDate(clamped);
    if (fromDate && clamped < fromDate) {
      setFromDate(clamped);
    }
  };

  useEffect(() => {
    let cancelled = false;

    const loadFilterOptions = async () => {
      const facilityId = localStorage.getItem(STORAGE_KEYS.FACILITY_ID) || undefined;
      const [facilitiesRes, radiologistsRes] = await Promise.all([
        facilityService.getAllFacilities(1),
        userService.getAllUsers(1, facilityId, { role: 'Radiologist', status: 'Active' }),
      ]);

      if (cancelled) {
        return;
      }

      const facilities = facilitiesRes.success && facilitiesRes.data ? facilitiesRes.data : [];
      setFacilityOptions([
        { value: '', label: 'All facilities' },
        ...facilities.map(f => ({
          value: String(f.id),
          label: f.facility_name || f.facility_code || `Facility ${f.id}`,
        })),
      ]);

      const radiologists =
        radiologistsRes.success && radiologistsRes.data ? radiologistsRes.data : [];
      setRadiologistOptions([
        { value: '', label: 'All radiologists' },
        ...radiologists.map(r => ({
          value: String(r.id),
          label: r.full_name || r.username || `Radiologist ${r.id}`,
        })),
      ]);
    };

    loadFilterOptions().catch(() => {
      if (!cancelled) {
        setFacilityOptions([{ value: '', label: 'All facilities' }]);
        setRadiologistOptions([{ value: '', label: 'All radiologists' }]);
      }
    });

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const timer = window.setTimeout(() => {
      setLoading(true);
      setError(null);
      setWarnings([]);
      getReportDeliveryAnalytics({
        startDate: fromDate,
        endDate: toDate,
        modality: modality || undefined,
        priority: priority || undefined,
        facilityId: facility || undefined,
        radiologistId: radiologist || undefined,
        channel: channel || undefined,
      })
        .then(res => {
          if (cancelled) {
            return;
          }
          if (res.success && res.data) {
            setSummary(res.data.summary);
            setChartPoints(res.data.chartPoints);
            setModalityRows(res.data.modalityRows);
            setDetailRows(res.data.detailRows || []);
            setWarnings(Array.isArray(res.data.meta?.warnings) ? res.data.meta.warnings : []);
          } else {
            setSummary(EMPTY_SUMMARY);
            setChartPoints([]);
            setModalityRows([]);
            setDetailRows([]);
            setWarnings([]);
            setError(res.error || 'Failed to load report delivery analytics');
          }
        })
        .catch(err => {
          if (cancelled) {
            return;
          }
          setSummary(EMPTY_SUMMARY);
          setChartPoints([]);
          setModalityRows([]);
          setDetailRows([]);
          setError(err instanceof Error ? err.message : 'Failed to load report delivery analytics');
        })
        .finally(() => {
          if (!cancelled) {
            setLoading(false);
          }
        });
    }, 300);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [fromDate, toDate, modality, priority, facility, radiologist, channel]);

  const user = authService.getUser();
  if (!canAccessPage(user, '/analytics/report-delivery')) {
    return (
      <Navigate
        to={getDefaultRoute(user)}
        replace
      />
    );
  }

  const handleExport = (format: AnalyticsExportFormat) => {
    exportReportDeliveryAnalytics(
      format,
      {
        summary,
        modalityRows,
        chartPoints,
        detailRows,
      },
      {
        startDate: fromDate,
        endDate: toDate,
        facilityLabel: labelForOption(facilityOptions, facility, 'All facilities'),
        modalityLabel: labelForOption(STATIC_FILTER_OPTIONS.modality, modality, 'All modalities'),
        priorityLabel: labelForOption(STATIC_FILTER_OPTIONS.priority, priority, 'All priorities'),
        radiologistLabel: labelForOption(radiologistOptions, radiologist, 'All radiologists'),
        deliveryChannelLabel: labelForOption(
          STATIC_FILTER_OPTIONS.channel,
          channel,
          'All channels'
        ),
      }
    );
  };

  return (
    <PageLayout
      title="Report Delivery Analytics"
      hideTitleInHeader
      contentClassName="rda-page-layout-content"
      breadcrumbs={[
        { label: 'Home', path: '/admin-dashboard' },
        { label: 'Report Delivery Analytics' },
      ]}
    >
      <div className="rda-dashboard">
        <header className="rda-dashboard-subheader">
          <h1 className="rda-dashboard-subheader-title">Report Delivery Analytics</h1>
        </header>

        <div className="rda-dashboard-container">
          <section
            className="rda-filters"
            aria-label="Report delivery filters"
          >
            <RdaFilterDate
              label="From"
              value={fromDate}
              onChange={handleFromDateChange}
              max={toDate && toDate < maxSelectableDate ? toDate : maxSelectableDate}
            />
            <RdaFilterDate
              label="To"
              value={toDate}
              onChange={handleToDateChange}
              min={fromDate || undefined}
              max={maxSelectableDate}
            />
            <RdaFilterSelect
              label="Facility"
              value={facility}
              onChange={setFacility}
              options={facilityOptions}
            />
            <RdaFilterSelect
              label="Modality"
              value={modality}
              onChange={setModality}
              options={STATIC_FILTER_OPTIONS.modality}
            />
            <RdaFilterSelect
              label="Priority"
              value={priority}
              onChange={setPriority}
              options={STATIC_FILTER_OPTIONS.priority}
            />
            <RdaFilterSelect
              label="Radiologist"
              value={radiologist}
              onChange={setRadiologist}
              options={radiologistOptions}
            />
            <RdaFilterSelect
              label="Delivery channel"
              value={channel}
              onChange={setChannel}
              options={STATIC_FILTER_OPTIONS.channel}
            />
          </section>

          {error && (
            <p
              className="rda-error"
              role="alert"
            >
              {error}
            </p>
          )}

          {!error && warnings.length > 0 && (
            <output className="rda-warning">
              {warnings.map(w => (
                <span key={w}>{w}</span>
              ))}
            </output>
          )}

          <section
            className="rda-summary-cards"
            aria-label="Summary metrics"
          >
            <div className="rda-summary-card">
              <h2 className="rda-summary-card-title">Total studies</h2>
              <p className="rda-summary-card-value">{loading ? '—' : summary.totalStudies}</p>
            </div>
            <div className="rda-summary-card">
              <h2 className="rda-summary-card-title">Total reports delivered</h2>
              <p className="rda-summary-card-value">
                {loading ? '—' : summary.totalReportsDelivered}
              </p>
            </div>
            <div className="rda-summary-card">
              <h2 className="rda-summary-card-title">Average delivery time</h2>
              <p className="rda-summary-card-value">
                {loading ? '—' : summary.averageDeliveryTimeFormatted}
              </p>
            </div>
            <div className="rda-summary-card">
              <h2 className="rda-summary-card-title">Delayed reports</h2>
              <p className="rda-summary-card-value">{loading ? '—' : summary.delayedReports}</p>
            </div>
          </section>

          <section
            className="rda-chart-card"
            aria-label="Overall delivery time chart"
          >
            <div className="rda-chart-header">
              <span
                className="rda-chart-icon"
                aria-hidden
              >
                <Clock
                  strokeWidth={ICON_STROKE_WIDTH}
                  size={20}
                />
              </span>
              <h2 className="rda-chart-title">Overall delivery time</h2>
            </div>
            {loading ? (
              <p className="rda-loading">Loading chart…</p>
            ) : chartPoints.length === 0 ? (
              <p className="rda-empty">No delivery data for the selected date range.</p>
            ) : (
              <DeliveryTimeLineChart
                points={chartPoints}
                yMax={chartAxis.yMax}
                yTicks={chartAxis.yTicks}
                averageMinutes={summary.averageDeliveryTimeMinutes}
              />
            )}
          </section>

          <section
            className="rda-table-section"
            aria-label="Modality breakdown"
          >
            <div className="rda-table-toolbar">
              <AnalyticsExportMenu
                onExport={handleExport}
                disabled={loading || (modalityRows.length === 0 && detailRows.length === 0)}
              />
            </div>
            <table className="rda-table">
              <thead>
                <tr>
                  <th scope="col">Modality</th>
                  <th scope="col">Total reports</th>
                  <th scope="col">Avg delivery time</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={3}>Loading…</td>
                  </tr>
                ) : modalityRows.length === 0 ? (
                  <tr>
                    <td colSpan={3}>No data for the selected filters.</td>
                  </tr>
                ) : (
                  modalityRows.map(row => (
                    <tr key={row.modality}>
                      <td>{row.modality}</td>
                      <td>{row.totalReports}</td>
                      <td>{row.avgDeliveryTimeFormatted}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </section>
        </div>
      </div>
    </PageLayout>
  );
}

export default ReportDeliveryAnalytics;
